import {
  baseUrl as defaultBaseUrl,
  doLogout,
  refreshAccessToken
} from "@/api/config";
import { STATUS_CODE } from "@/constants/errorCode";
import { ERROR_MESSAGE } from "@/constants/message";
import {
  DatasetDetailResponse,
  OperationStatus,
  OperationStatusType
} from "@/types/dataset";
import {
  CustomError,
  DATASET_STREAM_MAX_RETRIES,
  UnauthorizedStreamError
} from "@/utils/errors";

import { fetchEventSource } from "@microsoft/fetch-event-source";

/**
 * How far a pull's stream has been read. Kept across reconnects and, through
 * `onProgress`, across a page refresh.
 */
export type ConnectorPullProgress = {
  /** The operation reported success; the rows follow on dataset_data:done. */
  hasSucceeded: boolean;
  /** Id of the last event read, so a new stream carries on after it. */
  lastEventID?: string;
};

export type WatchConnectorPullOptions = {
  operationId: string;
  /** Aborting settles the returned promise; it never hangs. */
  signal: AbortSignal;
  baseUrl?: string;
  /** Where an earlier stream of this pull stopped, e.g. before a refresh. */
  resumeFrom?: ConnectorPullProgress;
  /**
   * Called after each dataset_operation_status event, to store how far the
   * stream got. Not after each row chunk: storing is a full rewrite, and a
   * resume from an older id only reads some chunks again, which are ignored.
   */
  onProgress?: (progress: ConnectorPullProgress) => void;
  /**
   * Called with the dataset as it stands after the pull, sent once the
   * operation succeeded and before its rows.
   */
  onDatasetDetail?: (dataset: DatasetDetailResponse) => void;
};

/** Delay between reconnects, matching what the stream hook waits. */
const RETRY_INTERVAL_MS = 5000;

/** A dropped connection, the only failure worth reconnecting for. */
class StreamDroppedError extends Error {
  constructor() {
    super("The connector pull stream dropped.");
    this.name = "StreamDroppedError";
  }
}

const toAbortError = (signal: AbortSignal): unknown =>
  (signal.reason as unknown) ?? new DOMException("Aborted", "AbortError");

const failed = (message?: string) =>
  new Error(message || ERROR_MESSAGE.SOMETHING_WENT_WRONG);

/** The stream url, starting after the last event read when there is one. */
const streamUrlOf = (
  operationId: string,
  baseUrl: string | undefined,
  lastEventID: string | undefined
) => {
  const url = `${baseUrl ?? defaultBaseUrl}/v1/datasets/operations/${operationId}/stream`;
  // Same query parameter the upload's parsing stream resumes with.
  return lastEventID
    ? `${url}?lastEventID=${encodeURIComponent(lastEventID)}`
    : url;
};

const streamOnce = (
  {
    operationId,
    signal,
    baseUrl,
    onProgress,
    onDatasetDetail
  }: WatchConnectorPullOptions,
  progress: ConnectorPullProgress
): Promise<void> =>
  new Promise((resolve, reject) => {
    // An already-aborted signal never fires `abort`, so without this guard the
    // promise would never settle.
    if (signal.aborted) {
      reject(toAbortError(signal));
      return;
    }

    const controller = new AbortController();

    signal.addEventListener(
      "abort",
      () => {
        controller.abort();
        // `fetchEventSource` skips `onerror` when its own controller is
        // aborted, so nothing else would ever settle this promise.
        reject(toAbortError(signal));
      },
      { once: true }
    );

    /** Whichever event ends the stream reports what the pull had reached. */
    const endOfStream = () => {
      if (progress.hasSucceeded) resolve();
      else reject(failed());
      controller.abort();
    };

    fetchEventSource(streamUrlOf(operationId, baseUrl, progress.lastEventID), {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
      openWhenHidden: true,

      async onopen(response: Response): Promise<void> {
        if (response.ok) return;

        if (response.status === STATUS_CODE.UNAUTHORIZED) {
          throw new UnauthorizedStreamError();
        }

        throw new CustomError(
          `Unexpected response status: ${response.status.toString()}`,
          response.status
        );
      },

      onmessage({ id, event, data }) {
        if (!data) return;

        switch (event) {
          // Sent repeatedly as the pull progresses.
          case "dataset_operation_status": {
            const operation = JSON.parse(data) as OperationStatus;

            // Success here only means the operation finished; the rows
            // themselves follow on dataset_data:done.
            if (operation.status === OperationStatusType.SUCCESS) {
              progress.hasSucceeded = true;
              break;
            }

            if (operation.status === OperationStatusType.FAILED) {
              reject(failed(operation.message));
              controller.abort();
            }
            break;
          }

          case "dataset_detail": {
            onDatasetDetail?.(JSON.parse(data) as DatasetDetailResponse);
            break;
          }

          // The rows have all landed; the payload is not needed to count them.
          case "dataset_data:done": {
            resolve();
            controller.abort();
            break;
          }

          case "stream:done":
          case "stream:error":
          case "stream:reset": {
            endOfStream();
            break;
          }
        }

        // stream:done carries no id; every other event does.
        if (id) progress.lastEventID = id;
        if (id && event === "dataset_operation_status") {
          onProgress?.({ ...progress });
        }
      },

      onerror(err) {
        // Reject, then rethrow so `fetchEventSource` disposes instead of
        // retrying on a one-second timer behind our back.
        if (err instanceof UnauthorizedStreamError) {
          reject(err);
          throw err;
        }

        const dropped = new StreamDroppedError();
        reject(dropped);
        throw dropped;
      }
    });
  });

const waitBeforeReconnect = (signal: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, RETRY_INTERVAL_MS);

    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
  });

/**
 * A pull left with no stream to read it on is only as settled as it already
 * was: a success reported before the last drop stands, anything less failed.
 */
const settleOutOfReconnects = (progress: ConnectorPullProgress) => {
  if (!progress.hasSucceeded) throw failed();
};

/** `streamOnce` plus the reconnects a dropped connection is owed. */
const streamWithReconnects = async (
  options: WatchConnectorPullOptions,
  progress: ConnectorPullProgress
): Promise<void> => {
  for (let attempt = 1; attempt <= DATASET_STREAM_MAX_RETRIES; attempt++) {
    try {
      return await streamOnce(options, progress);
    } catch (error) {
      if (!(error instanceof StreamDroppedError)) throw error;

      if (attempt === DATASET_STREAM_MAX_RETRIES) {
        settleOutOfReconnects(progress);
        return;
      }

      await waitBeforeReconnect(options.signal);
      if (options.signal.aborted) throw toAbortError(options.signal);
    }
  }
};

/**
 * Follows one connector pull to the end. The pull request only schedules the
 * work, so this is what says whether it landed: the promise resolves once the
 * rows are in, and rejects with the reason the operation or the stream gave.
 */
export const watchConnectorPull = async (
  options: WatchConnectorPullOptions
): Promise<void> => {
  const progress: ConnectorPullProgress = {
    hasSucceeded: false,
    ...options.resumeFrom
  };

  try {
    return await streamWithReconnects(options, progress);
  } catch (error) {
    if (!(error instanceof UnauthorizedStreamError)) throw error;

    await refreshAccessToken();

    try {
      return await streamWithReconnects(options, progress);
    } catch (retryError) {
      if (retryError instanceof UnauthorizedStreamError) await doLogout();
      throw retryError;
    }
  }
};
